import React, { useContext } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { useNavigate, useLocation } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_PORTAL_COMPANY,
  PAGE_PORTAL_DASHBOARD,
  PAGE_ROUTE_MEMBERSHIP_PLANS,
  PAGE_ROUTE_MEMBERSHIP_SUBSCRIPTIONS,
  PORTAL_PREFIX,
  routePathWithoutLocale,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import MembershipNavLink from "./MembershipNavLink";
import CustomProgressBar from "../common/CustomProgressBar/CustomProgressBar";
import AlertBannerText from "../common/AlertBanner/AlertBanner";
import { useOrganizationLabels } from "../../hooks/useOrganizationLabels";

interface TopbarProps {
  isAuthFlow?: boolean;
  openFlyOver?: () => void;
  showMenuIcon?: boolean;
  isMembership?: boolean;
  /** When `isMembership`, show Plans + Subscriptions (default true). Set false for routes like download-apps that only need Home + Portal. */
  showMembershipSubNav?: boolean;
}

const Topbar: React.FC<TopbarProps> = ({
  isAuthFlow,
  openFlyOver,
  showMenuIcon,
  isMembership,
  showMembershipSubNav = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useKivunovaTranslation();
  const org = useOrganizationLabels();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const pageTitle =
    location.pathname.split("/").filter(Boolean).pop() || "Home";
  const isCompanySegment = pageTitle.toLowerCase() === PAGE_PORTAL_COMPANY;
  const displayTitle =
    pageTitle === PAGE_PORTAL_DASHBOARD
      ? t("page-title-home", "Home")
      : isCompanySegment
        ? org.pageTitle
        : pageTitle;
  const topbarTitleUsesI18n =
    pageTitle === PAGE_PORTAL_DASHBOARD || isCompanySegment;

  const homePath =
    language && defaultLang ? localizedPath("/", language, defaultLang) : "/";

  const portalPath =
    language && defaultLang
      ? localizedPath(
          `${PORTAL_PREFIX}/${PAGE_PORTAL_DASHBOARD}`,
          language,
          defaultLang
        )
      : `${PORTAL_PREFIX}`;

  const membershipPlansPath =
    language && defaultLang
      ? localizedPath(PAGE_ROUTE_MEMBERSHIP_PLANS, language, defaultLang)
      : `/${PAGE_ROUTE_MEMBERSHIP_PLANS}`;
  const membershipSubscriptionsPath =
    language && defaultLang
      ? localizedPath(
          PAGE_ROUTE_MEMBERSHIP_SUBSCRIPTIONS,
          language,
          defaultLang
        )
      : `/${PAGE_ROUTE_MEMBERSHIP_SUBSCRIPTIONS}`;

  const onMembershipPlans = location.pathname.includes(
    `/${PAGE_ROUTE_MEMBERSHIP_PLANS}`
  );
  const onMembershipSubscriptions = location.pathname.includes(
    `/${PAGE_ROUTE_MEMBERSHIP_SUBSCRIPTIONS}`
  );

  const routePath = routePathWithoutLocale(location.pathname);
  const isOnHome = routePath === "";
  const isOnPortal = location.pathname.includes(`/${PORTAL_PREFIX}`);

  const navButtonSx = (active: boolean) => ({
    color: active ? "primary.main" : "text.primary",
    textTransform: "none" as const,
    fontWeight: active ? 700 : 600,
    minWidth: "auto",
    px: { xs: 1, sm: 1.5 },
    borderBottom: active ? "2px solid" : "2px solid transparent",
    borderColor: active ? "primary.main" : "transparent",
    borderRadius: 0,
    "&:hover": { backgroundColor: "action.hover" }
  });

  return (
    <AppBar
      position="relative"
      elevation={0}
      sx={{
        backgroundColor: "background.paper",
        color: "text.primary",
        borderBottom: "1px solid",
        borderColor: "divider",
        backdropFilter: "saturate(180%) blur(12px)",
        WebkitBackdropFilter: "saturate(180%) blur(12px)",
        // Capacitor / iOS notch & status bar — WebView is edge-to-edge; pad below system UI
        paddingTop: "env(safe-area-inset-top, 0px)"
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: isAuthFlow ? "end" : "space-between",
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 1.5, sm: 2 }
        }}
      >
        {!isAuthFlow && isMembership && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.75, sm: 1 },
              flexWrap: "wrap",
              rowGap: 0.75,
              p: 0.5,
              borderRadius: 3,
              bgcolor: alpha("#6f49c9", 0.05),
              border: `1px solid ${alpha("#6f49c9", 0.1)}`
            }}
          >
            <MembershipNavLink
              icon={<HomeOutlinedIcon sx={{ fontSize: 17 }} />}
              label={t("topbar-home", "Home")}
              active={isOnHome}
              onClick={() => navigate(homePath)}
            />
            <MembershipNavLink
              icon={<DashboardIcon sx={{ fontSize: 17 }} />}
              label={t("topbar-portal", "Portal")}
              active={isOnPortal}
              onClick={() => navigate(portalPath)}
            />
            {showMembershipSubNav && (
              <>
                <Button
                  size="small"
                  onClick={() => navigate(membershipPlansPath)}
                  sx={navButtonSx(onMembershipPlans)}
                >
                  {t("membership-nav-plans", "Plans")}
                </Button>
                <Button
                  size="small"
                  onClick={() => navigate(membershipSubscriptionsPath)}
                  sx={navButtonSx(onMembershipSubscriptions)}
                >
                  {t("membership-nav-subscriptions", "Subscriptions")}
                </Button>
              </>
            )}
          </Box>
        )}
        {!isAuthFlow && !isMembership && (
          <>
            {!showMenuIcon ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <IconButton
                    onClick={() => navigate(-1)}
                    size="small"
                    sx={{
                      color: "text.primary",
                      "&:hover": { backgroundColor: "action.hover" }
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </motion.div>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    textTransform: topbarTitleUsesI18n ? "none" : "capitalize",
                    letterSpacing: "-0.01em"
                  }}
                >
                  {displayTitle}
                </Typography>
              </Box>
            ) : (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  sx={{ mr: 1 }}
                  onClick={() => openFlyOver?.()}
                >
                  <MenuIcon />
                </IconButton>
              </motion.div>
            )}
          </>
        )}
        <LanguageSelector />
      </Toolbar>
      <CustomProgressBar />
      <Box>
        <AlertBannerText />
      </Box>
    </AppBar>
  );
};

export default Topbar;
